// @/lib/componentSchemaLib.ts

import {
  SchemaDefinition,
  ComponentType,
  ComponentTypes,
  MetadataField,
} from "@/types/diagram";
import { parse as parseYaml } from "yaml";

// Define required default fields that must exist in all component types
const DEFAULT_REQUIRED_FIELDS: MetadataField[] = [
  {
    name: "name",
    type: "string",
    label: "Name",
    required: true,
  },
  {
    name: "description",
    type: "string",
    label: "Description",
    required: false,
  },
  {
    name: "lastModified",
    type: "string",
    label: "Last Modified",
    required: false,
  },
];

const DEFAULT_SCHEMA: SchemaDefinition = {
  componentTypes: {
    basic: {
      displayName: "Component",
      fields: DEFAULT_REQUIRED_FIELDS,
    },
    service: {
      displayName: "Service",
      fields: [
        {
          name: "name",
          type: "string",
          label: "Name",
          required: true,
        },
        {
          name: "description",
          type: "string",
          label: "Description",
          required: false,
        },
        {
          name: "lastModified",
          type: "string",
          label: "Last Modified",
          required: false,
        },
        {
          name: "serviceName",
          type: "string",
          label: "Service Name",
          required: true,
        },
        {
          name: "version",
          type: "string",
          label: "Version",
          required: false,
        },
        {
          name: "owner",
          type: "string",
          label: "Service Owner",
          required: false,
        },
        {
          name: "status",
          type: "select",
          label: "Status",
          required: false,
          options: [
            { label: "Active", value: "active" },
            { label: "Deprecated", value: "deprecated" },
            { label: "Planning", value: "planning" },
          ],
        },
      ],
    },
    database: {
      displayName: "Database",
      fields: [
        {
          name: "name",
          type: "string",
          label: "Name",
          required: true,
        },
        {
          name: "description",
          type: "string",
          label: "Description",
          required: false,
        },
        {
          name: "lastModified",
          type: "string",
          label: "Last Modified",
          required: false,
        },
        {
          name: "databaseName",
          type: "string",
          label: "Database Name",
          required: true,
        },
        {
          name: "engine",
          type: "select",
          label: "Database Engine",
          required: false,
          options: [
            { label: "PostgreSQL", value: "postgres" },
            { label: "MySQL", value: "mysql" },
            { label: "MongoDB", value: "mongodb" },
          ],
        },
        {
          name: "version",
          type: "string",
          label: "Engine Version",
          required: false,
        },
      ],
    },
    layer: {
      displayName: "Layer",
      fields: [
        {
          name: "name",
          type: "string",
          label: "Name",
          required: true,
        },
        {
          name: "description",
          type: "string",
          label: "Description",
          required: false,
        },
        {
          name: "labelPosition",
          type: "select",
          label: "Label Position",
          required: false,
          options: [
            { label: "front-left", value: "front-left" },
            { label: "front-right", value: "front-right" },
            { label: "back-left", value: "back-left" },
            { label: "back-right", value: "back-right" },
          ],
        },
        {
          name: "lastModified",
          type: "string",
          label: "Last Modified",
          required: false,
        },
      ],
    },
  },
};

class SchemaLoader {
  private static instance: SchemaLoader;
  private loadedSchema: SchemaDefinition;

  private constructor() {
    this.loadedSchema = DEFAULT_SCHEMA;
    this.initializeFromYaml();
  }

  private async initializeFromYaml(): Promise<void> {
    try {
      // Try to load from the local YAML file
      const response = await fetch('/src/lib/component-types.yaml');
      if (response.ok) {
        const yamlContent = await response.text();
        const schema = await this.parseSchema(yamlContent);
        if (schema) {
          const validatedSchema = this.validateAndEnhanceSchema(schema);
          if (validatedSchema) {
            this.loadedSchema = validatedSchema;
          }
        }
      }
    } catch (error) {
      // Silently fall back to DEFAULT_SCHEMA if YAML loading fails
      console.warn('Could not load component-types.yaml, using default schema');
    }
  }

  public static getInstance(): SchemaLoader {
    if (!SchemaLoader.instance) {
      SchemaLoader.instance = new SchemaLoader();
    }
    return SchemaLoader.instance;
  }

  private async parseSchema(content: string): Promise<SchemaDefinition | null> {
    try {
      const yamlSchema = parseYaml(content);
      return yamlSchema as SchemaDefinition;
    } catch (yamlError) {
      try {
        return JSON.parse(content) as SchemaDefinition;
      } catch (jsonError) {
        return null;
      }
    }
  }

  private isValidField(field: any): field is MetadataField {
    if (!field || typeof field !== "object") return false;
    if (!field.name || typeof field.name !== "string") return false;
    if (!field.type || !["string", "select"].includes(field.type)) return false;
    if (!field.label || typeof field.label !== "string") return false;

    if (field.type === "select") {
      if (!Array.isArray(field.options)) return false;
      for (const option of field.options) {
        if (!option || typeof option !== "object") return false;
        if (!option.label || typeof option.label !== "string") return false;
        if (!option.value || typeof option.value !== "string") return false;
      }
    }

    return true;
  }

  private hasRequiredDefaultFields(fields: MetadataField[]): boolean {
    return DEFAULT_REQUIRED_FIELDS.every((defaultField) => {
      const matchingField = fields.find((f) => f.name === defaultField.name);
      if (!matchingField) return false;

      // Check if required field properties match
      return (
        matchingField.type === defaultField.type &&
        matchingField.label === defaultField.label &&
        (defaultField.required ? matchingField.required === true : true)
      );
    });
  }

  private ensureRequiredDefaultFields(
    fields: MetadataField[]
  ): MetadataField[] {
    const updatedFields = [...fields];

    DEFAULT_REQUIRED_FIELDS.forEach((defaultField) => {
      const existingFieldIndex = updatedFields.findIndex(
        (f) => f.name === defaultField.name
      );

      if (existingFieldIndex === -1) {
        // Add missing default field
        updatedFields.unshift(defaultField);
      } else {
        // Update existing field to match default requirements
        updatedFields[existingFieldIndex] = {
          ...updatedFields[existingFieldIndex],
          type: defaultField.type,
          label: defaultField.label,
          required:
            defaultField.required || updatedFields[existingFieldIndex].required,
        };
      }
    });

    return updatedFields;
  }

  private isValidComponentType(type: unknown): type is ComponentType {
    if (!type || typeof type !== "object") return false;

    const componentType = type as Record<string, unknown>;

    if (
      !("displayName" in componentType) ||
      typeof componentType.displayName !== "string"
    ) {
      return false;
    }

    if (!("fields" in componentType) || !Array.isArray(componentType.fields)) {
      return false;
    }

    // Validate each field
    if (!componentType.fields.every((field) => this.isValidField(field))) {
      return false;
    }

    // Validate required default fields
    return this.hasRequiredDefaultFields(componentType.fields);
  }

  private validateAndEnhanceSchema(schema: any): SchemaDefinition | null {
    if (!schema || typeof schema !== "object") return null;
    if (!schema.componentTypes || typeof schema.componentTypes !== "object")
      return null;

    const componentTypes = schema.componentTypes as Record<string, unknown>;
    const enhancedComponentTypes: ComponentTypes = {};

    // Process each component type
    for (const [key, type] of Object.entries(componentTypes)) {
      if (!this.isValidComponentType(type)) {
        const componentType = type as any;

        // Try to fix the component type by adding required fields
        if (componentType.displayName && Array.isArray(componentType.fields)) {
          const enhancedFields = this.ensureRequiredDefaultFields(
            componentType.fields
          );
          enhancedComponentTypes[key] = {
            displayName: componentType.displayName,
            fields: enhancedFields,
          };
        } else {
          return null;
        }
      } else {
        enhancedComponentTypes[key] = type as ComponentType;
      }
    }

    return { componentTypes: enhancedComponentTypes };
  }

  public async loadSchema(schemaUrl: string): Promise<SchemaDefinition> {
    try {
      const response = await fetch(schemaUrl);
      if (!response.ok) {
        return this.loadedSchema;
      }

      const content = await response.text();
      const schema = await this.parseSchema(content);

      if (!schema) {
        return this.loadedSchema;
      }

      const validatedSchema = this.validateAndEnhanceSchema(schema);
      if (!validatedSchema) {
        return this.loadedSchema;
      }

      // Merge with default schema
      this.loadedSchema = {
        componentTypes: {
          ...DEFAULT_SCHEMA.componentTypes,
          ...validatedSchema.componentTypes,
        },
      };

      return this.loadedSchema;
    } catch (error) {
      return this.loadedSchema;
    }
  }

  public getSchema(): SchemaDefinition {
    return this.loadedSchema;
  }

  public getComponentType(type: string) {
    return this.loadedSchema.componentTypes[type];
  }

  public getAvailableTypes(): string[] {
    return Object.keys(this.loadedSchema.componentTypes);
  }

  public async loadFromYamlFile(): Promise<void> {
    await this.initializeFromYaml();
  }
}

export const schemaLoader = SchemaLoader.getInstance();

export type { SchemaLoader };
